/**
 * /admin/quotes
 *
 * Admin page for managing quotes. Add new quotes, edit existing ones,
 * toggle active/inactive, delete.
 */

import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../../api/client.js';
import {
  listAdminQuotes,
  createQuote,
  updateQuote,
  deleteQuote,
} from '../../api/quotes.js';
import { usePageTitle } from '../../hooks/usePageTitle.js';
import './QuotesAdminPage.css';


export default function QuotesAdminPage() {
  usePageTitle('Admin · Quotes');

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // add form
  const [newBody, setNewBody] = useState('');
  const [newAttribution, setNewAttribution] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');

  const loadQuotes = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setQuotes(await listAdminQuotes());
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.detail : 'failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadQuotes(); }, [loadQuotes]);

  async function handleAdd(e) {
    e.preventDefault();
    if (!newBody.trim() || adding) return;
    setAdding(true);
    setAddError('');
    try {
      await createQuote({
        body: newBody.trim(),
        attribution: newAttribution.trim() || null,
      });
      setNewBody('');
      setNewAttribution('');
      loadQuotes();
    } catch (err) {
      setAddError(err instanceof ApiError ? err.detail : 'failed to add');
    } finally {
      setAdding(false);
    }
  }

  const activeCount = quotes.filter((q) => q.is_active).length;

  return (
    <>
      <div className="admin-page-head">
        <div className="admin-crumbs">
          <a href="/admin">admin</a>
          <span className="sep">/</span>
          quotes
        </div>
        <div className="admin-section-head">
          <div>
            <h1 className="admin-section-title">Quotes</h1>
            <p className="admin-section-sub">
              wisdom, humor, and things taken out of context
            </p>
          </div>
        </div>
      </div>

      <div className="admin-content">
        {/* ── add form ── */}
        <section className="qa-form-card">
          <div className="qa-form-title">Add a quote</div>
          <form onSubmit={handleAdd} className="qa-form">
            <textarea
              className="qa-form-body"
              placeholder="the quote itself…"
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
              rows={3}
            />
            <div className="qa-form-row">
              <input
                type="text"
                className="qa-form-attr"
                placeholder="attribution (optional)"
                value={newAttribution}
                onChange={(e) => setNewAttribution(e.target.value)}
              />
              <button
                type="submit"
                className="admin-btn admin-btn-primary"
                disabled={adding || !newBody.trim()}
              >
                {adding ? 'adding…' : '+ add quote'}
              </button>
            </div>
            {addError && <div className="qa-error">⚠ {addError}</div>}
          </form>
        </section>

        {/* ── list ── */}
        <section className="qa-list-section">
          <div className="qa-list-head">
            <h2 className="qa-list-title">
              All quotes
              {!loading && (
                <span className="qa-count">
                  {activeCount} active · {quotes.length} total
                </span>
              )}
            </h2>
          </div>

          {loading ? (
            <div className="qa-empty">loading…</div>
          ) : loadError ? (
            <div className="qa-empty error">⚠ {loadError}</div>
          ) : quotes.length === 0 ? (
            <div className="qa-empty">no quotes yet. add one above.</div>
          ) : (
            <div className="qa-list">
              {quotes.map((q) => (
                <QuoteRow key={q.id} quote={q} onUpdate={loadQuotes} />
              ))}
            </div>
          )}
        </section>
      </div>
    </>
  );
}


function QuoteRow({ quote, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(quote.body);
  const [attribution, setAttribution] = useState(quote.attribution || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await updateQuote(quote.id, {
        body: body.trim(),
        attribution: attribution.trim() || null,
      });
      setEditing(false);
      onUpdate();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive() {
    try {
      await updateQuote(quote.id, { is_active: !quote.is_active });
      onUpdate();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'failed to update');
    }
  }

  async function handleDelete() {
    if (!confirm('Delete this quote permanently?')) return;
    try {
      await deleteQuote(quote.id);
      onUpdate();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'failed to delete');
    }
  }

  return (
    <div className={`qa-row ${!quote.is_active ? 'inactive' : ''}`}>
      {editing ? (
        <div className="qa-row-edit">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            className="qa-edit-body"
          />
          <input
            type="text"
            value={attribution}
            onChange={(e) => setAttribution(e.target.value)}
            placeholder="attribution"
            className="qa-edit-attr"
          />
          {error && <div className="qa-error">⚠ {error}</div>}
          <div className="qa-row-actions">
            <button
              className="admin-btn admin-btn-primary"
              onClick={handleSave}
              disabled={saving || !body.trim()}
            >
              {saving ? 'saving…' : 'save'}
            </button>
            <button
              className="admin-btn admin-btn-ghost"
              onClick={() => {
                setEditing(false);
                setBody(quote.body);
                setAttribution(quote.attribution || '');
              }}
            >
              cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="qa-row-body">{quote.body}</div>
          {quote.attribution && (
            <div className="qa-row-attr">— {quote.attribution}</div>
          )}
          {error && <div className="qa-error">⚠ {error}</div>}
          <div className="qa-row-actions">
            <button
              className="admin-btn admin-btn-ghost"
              onClick={() => setEditing(true)}
            >
              edit
            </button>
            <button
              className="admin-btn admin-btn-ghost"
              onClick={handleToggleActive}
            >
              {quote.is_active ? 'deactivate' : 'activate'}
            </button>
            <button
              className="admin-btn admin-btn-danger-ghost"
              onClick={handleDelete}
            >
              delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}
